import { useState } from 'react';
import { C, SectionLabel, H2, BtnOrange, wrap, useIsMobile } from '../styles';

const WEBHOOK_URL = 'https://hook.us2.make.com/oiqmyu36y9p299u5nxdhgk6icdvnnl66';

const MAX_SIZE = 4 * 1024 * 1024; // 4MB limit for webhook
const MAX_DIMENSION = 1600; // max width or height in pixels

const points = [
  { t: 'No CAD required', b: 'A hand drawing or photograph is sufficient. We handle the translation into a structured fabrication brief.' },
  { t: 'Confidential by default', b: 'Project details are not shared with fabricators until you have reviewed and approved the brief.' },
  { t: 'Made by the right shop', b: 'Your project goes to shops with proven experience in the exact process and material it needs.' },
];

// Compress image using canvas
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;

        // Scale down if too large
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width > height) {
            height = Math.round((height * MAX_DIMENSION) / width);
            width = MAX_DIMENSION;
          } else {
            width = Math.round((width * MAX_DIMENSION) / height);
            height = MAX_DIMENSION;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Try different quality levels until under 4MB
        let quality = 0.85;
        let base64 = canvas.toDataURL('image/jpeg', quality).split(',')[1];

        while (base64.length * 0.75 > MAX_SIZE && quality > 0.3) {
          quality -= 0.1;
          base64 = canvas.toDataURL('image/jpeg', quality).split(',')[1];
        }

        resolve({ base64, type: 'image/jpeg' });
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Validation rules. `description`, `fullName` and `location` are each required on
// their own; `phone` and `email` are required only as a pair — one of the two is
// enough. Returns a message or '' for each field that is currently invalid.
const validateField = (name, form) => {
  switch (name) {
    case 'description':
      return form.description.trim() ? '' : 'Tell us roughly what you need made.';
    case 'fullName':
      return form.fullName.trim() ? '' : 'We need a name to reply to.';
    case 'location':
      return form.location ? '' : 'Pick the closest region.';
    case 'phone':
    case 'email':
      return form.phone.trim() || form.email.trim()
        ? ''
        : 'Add a phone number or an email so we can reach you.';
    default:
      return '';
  }
};

const FIELDS = ['description', 'phone', 'fullName', 'email', 'location'];

const FieldLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} style={{
    display: 'block', fontSize: 12, fontWeight: 600, color: C.txt,
    marginBottom: 6, letterSpacing: '0.01em',
  }}>{children}</label>
);

const FieldHint = ({ children }) => (
  <p style={{ fontSize: 11, color: C.dim, lineHeight: 1.5, marginTop: 5 }}>{children}</p>
);

// Inline, next to the field it belongs to — no error summary at the top of the
// form and nothing flashes red on submit.
const FieldError = ({ msg }) => (
  msg ? <p role="alert" style={{ fontSize: 11, color: C.orange, marginTop: 5 }}>{msg}</p> : null
);

export default function SubmitForm() {
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', location: '', company: '',
    projectType: '', description: '', quantity: '', timeline: '', budget: ''
  });
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [status, setStatus] = useState('idle');
  const [showMore, setShowMore] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const isMobile = useIsMobile();

  const handle = (e) => {
    const next = { ...form, [e.target.name]: e.target.value };
    setForm(next);
    // Only clear errors while typing — never introduce one mid-keystroke.
    setErrors((prev) => {
      if (!prev[e.target.name] && !(prev.phone || prev.email)) return prev;
      const cleared = { ...prev };
      const msg = validateField(e.target.name, next);
      if (!msg) delete cleared[e.target.name];
      // phone/email share one rule, so satisfying either clears both.
      if ((e.target.name === 'phone' || e.target.name === 'email') && !validateField('phone', next)) {
        delete cleared.phone; delete cleared.email;
      }
      return cleared;
    });
  };

  const handleBlur = (e) => {
    const name = e.target.name;
    setTouched((t) => ({ ...t, [name]: true }));
    const msg = validateField(name, form);
    setErrors((prev) => {
      const next = { ...prev };
      if (msg) next[name] = msg; else delete next[name];
      return next;
    });
  };

  const errStyle = (f) => (errors[f] ? { borderColor: C.orange } : {});

  const handleFile = async (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    // PDFs pass through without compression
    if (selected.type === 'application/pdf') {
      if (selected.size > 5 * 1024 * 1024) {
        alert('PDF is too large. Please upload a PDF under 5MB.');
        return;
      }
      setFileName(selected.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setFile({ name: selected.name, type: selected.type, data: base64 });
      };
      reader.readAsDataURL(selected);
      return;
    }

    // Images get compressed
    if (!selected.type.startsWith('image/')) {
      alert('Please upload an image (JPEG, PNG, GIF, WEBP) or PDF.');
      return;
    }

    setFileName(selected.name);
    setStatus('compressing');

    try {
      const { base64, type } = await compressImage(selected);
      setFile({ name: selected.name, type, data: base64 });
      console.log('File compressed:', selected.name, 'Final base64 size:', Math.round(base64.length * 0.75 / 1024), 'KB');
      setStatus('idle');
    } catch (err) {
      console.error('Compression failed:', err);
      alert('Could not process image. Please try a different file.');
      setStatus('idle');
    }
  };

  const submit = async () => {
    // Validate everything at once, but show the errors inline next to each field
    // rather than as a summary — and keep every value the visitor entered.
    const found = {};
    FIELDS.forEach((f) => {
      const msg = validateField(f, form);
      if (msg) found[f] = msg;
    });
    // phone/email share a rule; surface it once, on phone.
    if (found.phone && found.email) delete found.email;
    if (Object.keys(found).length) {
      setErrors(found);
      setTouched(FIELDS.reduce((a, f) => ({ ...a, [f]: true }), {}));
      return;
    }
    // Geographic gate — MetalMatch currently serves Ontario, Canada only.
    if (form.location === 'Outside Ontario') {
      setStatus('outside');
      return;
    }
    if (fileName && !file) {
      alert('Please wait for the file to finish loading before submitting.');
      return;
    }
    setStatus('sending');
    try {
      await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          submittedAt: new Date().toISOString(),
          sketchData: file ? file.data : '',
          sketchName: file ? file.name : '',
          sketchType: file ? file.type : '',
        }),
      });
      setStatus('success');
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead');
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', { form_location: form.location || 'unspecified' });
      }
      setForm({ fullName: '', email: '', phone: '', location: '', company: '', projectType: '', description: '', quantity: '', timeline: '', budget: '' });
      setFile(null);
      setFileName('');
      setErrors({});
      setTouched({});
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <section id="submit" style={{ padding: '88px 0', borderTop: `1px solid ${C.bdr}`, background: C.surf }}>
      <div style={wrap}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1.1fr', gap: isMobile ? 32 : 52, alignItems: 'start' }}>
          <div>
            <SectionLabel orange>Submit your project</SectionLabel>
            <H2 style={{ marginBottom: 18 }}>Tell us what you need fabricated.</H2>
            <p style={{ color: C.mut, fontSize: 15, lineHeight: 1.75, marginBottom: 32 }}>
              A rough description and a sketch is all that is required. Our team will follow up within one business day to confirm details and begin brief preparation.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {points.map(({ t, b }) => (
                <div key={t} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.orange, flexShrink: 0, marginTop: 7 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.txt, marginBottom: 2 }}>{t}</div>
                    <p style={{ fontSize: 12, color: C.mut, lineHeight: 1.6 }}>{b}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: C.surf2, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: 28 }}>
            {status === 'success' ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
                <h3 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 28, fontWeight: 700, textTransform: 'uppercase', color: C.txt, marginBottom: 12 }}>Project Submitted</h3>
                <p style={{ color: C.mut, fontSize: 14, lineHeight: 1.7 }}>
                  We'll come back to you within one business day, and your brief will be ready inside 48–72 hours.
                </p>
              </div>
            ) : status === 'outside' ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>📍</div>
                <h3 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 28, fontWeight: 700, textTransform: 'uppercase', color: C.txt, marginBottom: 12 }}>Ontario Only, For Now</h3>
                <p style={{ color: C.mut, fontSize: 14, lineHeight: 1.7 }}>
                  MetalMatch currently serves Ontario, Canada only, so we aren't able to match projects outside the province yet. We're expanding — check back soon.
                </p>
                <button
                  type="button"
                  onClick={() => { setForm(f => ({ ...f, location: '' })); setStatus('idle'); }}
                  style={{ marginTop: 18, background: 'none', border: `1px solid ${C.bdr2}`, borderRadius: 6, color: C.mut, fontFamily: "'Instrument Sans', sans-serif", fontSize: 13, fontWeight: 600, padding: '10px 16px', cursor: 'pointer' }}
                >
                  ← Back to form
                </button>
              </div>
            ) : (
              <div>
                {/* Field order is deliberate: the question the visitor already has an
                    answer to comes first, and answering it creates the commitment that
                    carries them through the contact fields. */}
                <div style={{ marginBottom: 14 }}>
                  <FieldLabel htmlFor="mm-description">What do you need made?</FieldLabel>
                  <textarea
                    id="mm-description" name="description" rows={3}
                    value={form.description} onChange={handle} onBlur={handleBlur}
                    aria-required="true" aria-invalid={!!errors.description}
                    placeholder="Rough description is fine — what it is, roughly how big, what material if you know, and how many."
                    style={{ minHeight: 92, resize: 'vertical', ...errStyle('description') }}
                  />
                  <FieldError msg={errors.description} />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <FieldLabel htmlFor="mm-phone">Phone or mobile</FieldLabel>
                  <input
                    id="mm-phone" name="phone" type="tel" autoComplete="tel"
                    value={form.phone} onChange={handle} onBlur={handleBlur}
                    aria-required="true" aria-invalid={!!errors.phone}
                    style={errStyle('phone')}
                  />
                  <FieldHint>Fastest way to reach you. We call or text — whichever you prefer.</FieldHint>
                  <FieldError msg={errors.phone} />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <FieldLabel htmlFor="mm-name">Your name</FieldLabel>
                  <input
                    id="mm-name" name="fullName" autoComplete="name"
                    value={form.fullName} onChange={handle} onBlur={handleBlur}
                    aria-required="true" aria-invalid={!!errors.fullName}
                    style={errStyle('fullName')}
                  />
                  <FieldError msg={errors.fullName} />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <FieldLabel htmlFor="mm-email">Email <span style={{ color: C.dim, fontWeight: 400 }}>(optional)</span></FieldLabel>
                  <input
                    id="mm-email" name="email" type="email" autoComplete="email"
                    value={form.email} onChange={handle} onBlur={handleBlur}
                    style={errStyle('email')}
                  />
                  <FieldError msg={errors.email} />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <FieldLabel htmlFor="mm-location">Where's the project?</FieldLabel>
                  <select
                    id="mm-location" name="location"
                    value={form.location} onChange={handle} onBlur={handleBlur}
                    aria-required="true" aria-invalid={!!errors.location}
                    style={errStyle('location')}
                  >
                    <option value="">Select a region</option>
                    <option>Greater Toronto Area</option>
                    <option>Hamilton / Niagara</option>
                    <option>Kitchener–Waterloo</option>
                    <option>Ottawa</option>
                    <option>London / Southwestern Ontario</option>
                    <option>Other Ontario</option>
                    <option>Outside Ontario</option>
                  </select>
                  <FieldError msg={errors.location} />
                </div>

                {/* Collapsed by default. The upload is optional and always was, but as a
                    large dashed drop-zone above the submit button it read as a required
                    step. Same functionality, no longer looming. */}
                <button
                  type="button"
                  onClick={() => setShowUpload((v) => !v)}
                  aria-expanded={showUpload}
                  aria-controls="mm-upload-panel"
                  style={{
                    width: '100%', background: 'none', border: `1px solid ${C.bdr}`, borderRadius: 6,
                    color: C.mut, fontFamily: "'Instrument Sans', sans-serif", fontSize: 13, fontWeight: 600,
                    padding: '12px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10,
                    textAlign: 'left',
                  }}
                >
                  <span style={{
                    color: C.orange, fontSize: 11, display: 'inline-block',
                    transform: showUpload ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s',
                  }}>▶</span>
                  {fileName ? `Attached: ${fileName}` : 'Add a drawing or photo (optional)'}
                </button>

                {showUpload && (
                  <div id="mm-upload-panel" style={{ marginBottom: 12 }}>
                    <p style={{ fontSize: 12, color: C.mut, lineHeight: 1.6, marginBottom: 10 }}>
                      A phone photo of a napkin sketch is enough. You can also send it later — we'll reply with a place to upload.
                    </p>
                    <label
                      style={{
                        display: 'block',
                        border: `1.5px dashed ${fileName ? C.navy : C.bdr2}`,
                        borderRadius: 6, padding: 18, textAlign: 'center', cursor: 'pointer',
                        marginBottom: 8, transition: 'border-color 0.2s',
                        background: fileName ? C.navybg : 'transparent',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.orange; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = fileName ? C.navy : C.bdr2; }}
                    >
                      <div style={{ fontSize: 20, marginBottom: 4 }}>{fileName ? '📎' : '✎'}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.txt }}>
                        {status === 'compressing' ? 'Compressing image...' : fileName ? fileName : 'Choose a file'}
                      </div>
                      <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
                        {fileName ? 'Click to change file' : 'JPEG, PNG, GIF, WEBP or PDF · Max 10MB'}
                      </div>
                      <input
                        type="file"
                        style={{ display: 'none' }}
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,.pdf"
                        onChange={handleFile}
                      />
                    </label>
                    <p style={{ fontSize: 11, color: C.dim, textAlign: 'center' }}>
                      For multi-page drawings or package quotes, email us directly at{' '}
                      <a href="mailto:quotes@getmetalmatch.com" style={{ color: C.orange, textDecoration: 'none' }}>
                        quotes@getmetalmatch.com
                      </a>
                    </p>
                  </div>
                )}

                {/* Optional details — collapsed by default to keep the form short */}
                <button
                  type="button"
                  onClick={() => setShowMore(s => !s)}
                  aria-expanded={showMore}
                  style={{
                    width: '100%', background: 'none', border: `1px dashed ${C.bdr2}`, borderRadius: 6,
                    color: C.mut, fontFamily: "'Instrument Sans', sans-serif", fontSize: 13, fontWeight: 600,
                    padding: '12px 14px', marginBottom: 12, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 8,
                  }}
                >
                  <span style={{ color: C.orange, fontSize: 16 }}>{showMore ? '−' : '+'}</span>
                  {showMore ? 'Hide extra details' : 'Add more details (optional)'}
                </button>

                {showMore && (
                  <div style={{ marginBottom: 4 }}>
                    <div style={{ marginBottom: 10 }}>
                      <input name="company" value={form.company} onChange={handle} placeholder="Company (if applicable)" />
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <select name="projectType" value={form.projectType} onChange={handle}>
                        <option value="">Select project type</option>
                        <option>Custom metal part</option>
                        <option>Prototype / first run</option>
                        <option>CNC machined component</option>
                        <option>Stainless fixture or shelving</option>
                        <option>Recurring production order</option>
                        <option>Welded assembly</option>
                        <option>Not sure yet</option>
                      </select>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 10 }}>
                      <input name="quantity" value={form.quantity} onChange={handle} placeholder="Quantity" />
                      <input name="timeline" value={form.timeline} onChange={handle} placeholder="Timeline" />
                      <input name="budget" value={form.budget} onChange={handle} placeholder="Budget range" />
                    </div>
                  </div>
                )}

                <BtnOrange
                  onClick={submit}
                  disabled={status === 'compressing'}
                  style={{ width: '100%', justifyContent: 'center', padding: 15, opacity: status === 'sending' || status === 'compressing' ? 0.7 : 1 }}
                >
                  {status === 'sending' ? 'Sending...' : status === 'compressing' ? 'Processing image...' : 'Send My Project →'}
                </BtnOrange>
                {status === 'error' && (
                  <p style={{ fontSize: 12, color: C.orange, textAlign: 'center', marginTop: 10 }}>
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}
                <p style={{ fontSize: 12, color: C.mut, textAlign: 'center', marginTop: 12 }}>
                  Free to submit. No obligation. We reply within one business day.
                </p>
                <p style={{ fontSize: 11, color: C.dim, textAlign: 'center', marginTop: 8 }}>
                  By submitting, you agree to be contacted regarding your project. Your drawings are stored securely and remain confidential.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
